import React, { useState, useEffect } from 'react';
import { fetchChangelog, type ChangelogVersion, getSectionIcon, getSectionColor } from '../../utils/changelogParser';
import { Calendar, GitBranch, Package } from 'lucide-react';

export const ChangelogTimeline: React.FC = () => {
    const [versions, setVersions] = useState<ChangelogVersion[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedVersion, setSelectedVersion] = useState<ChangelogVersion | null>(null);

    useEffect(() => {
        loadChangelog();
    }, []);

    const loadChangelog = async () => {
        setLoading(true);
        const data = await fetchChangelog();
        setVersions(data);
        setLoading(false);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: 'var(--accent-color)' }}></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-serif font-bold flex items-center gap-2" style={{ color: 'var(--accent-glow)' }}>
                    <GitBranch className="w-6 h-6" />
                    Timeline des Versions
                </h2>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {versions.length} version{versions.length > 1 ? 's' : ''}
                </div>
            </div>

            {/* Timeline */}
            <div className="relative">
                {/* Vertical line */}
                <div className="absolute left-8 top-0 bottom-0 w-0.5" style={{ backgroundColor: 'var(--border-color)' }}></div>

                {/* Versions */}
                <div className="space-y-8">
                    {versions.map((version: ChangelogVersion, index: number) => (
                        <div key={version.version} className="relative pl-20">
                            {/* Timeline dot */}
                            <div
                                className="absolute left-6 top-2 w-5 h-5 rounded-full border-4 z-10"
                                style={{
                                    backgroundColor: 'var(--accent-color)',
                                    borderColor: 'var(--bg-primary)'
                                }}
                            ></div>

                            {/* Version card */}
                            <div
                                className={`card-navy border-l-4 cursor-pointer transition-all hover:shadow-xl ${selectedVersion?.version === version.version ? 'ring-2' : ''
                                    }`}
                                style={{
                                    borderLeftColor: 'var(--accent-color)',
                                    ...(selectedVersion?.version === version.version && {
                                        boxShadow: `0 0 0 2px var(--accent-color)`
                                    })
                                }}
                                onClick={() => setSelectedVersion(selectedVersion?.version === version.version ? null : version)}
                            >
                                {/* Header */}
                                <div className="flex justify-between items-start mb-4">
                                    <div>
                                        <div className="flex items-center gap-3 mb-2">
                                            <Package className="w-5 h-5" style={{ color: 'var(--accent-color)' }} />
                                            <h3 className="text-xl font-bold font-mono" style={{ color: 'var(--text-primary)' }}>
                                                v{version.version}
                                            </h3>
                                            {index === 0 && (
                                                <span
                                                    className="px-2 py-0.5 text-xs font-bold rounded"
                                                    style={{
                                                        backgroundColor: 'var(--accent-color)',
                                                        color: 'var(--bg-primary)'
                                                    }}
                                                >
                                                    LATEST
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                                            <Calendar className="w-4 h-4" />
                                            {version.date}
                                        </div>
                                    </div>
                                    <button
                                        className="text-xs font-medium px-3 py-1 rounded transition-colors"
                                        style={{
                                            color: 'var(--accent-color)',
                                            border: `1px solid var(--border-color)`
                                        }}
                                    >
                                        {selectedVersion?.version === version.version ? 'Réduire' : 'Détails'}
                                    </button>
                                </div>

                                {/* Summary */}
                                <div className="space-y-2">
                                    {Object.entries(version.sections).map(([sectionName, items]) => (
                                        <div key={sectionName} className="flex items-start gap-2">
                                            <span className="text-lg">{getSectionIcon(sectionName as any)}</span>
                                            <div>
                                                <span className="font-bold text-sm" style={{ color: getSectionColor(sectionName as any) }}>
                                                    {sectionName}
                                                </span>
                                                <span className="text-sm ml-2" style={{ color: 'var(--text-secondary)' }}>
                                                    ({items.length} {items.length > 1 ? 'items' : 'item'})
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                {/* Expanded Details */}
                                {selectedVersion?.version === version.version && (
                                    <div className="mt-6 pt-6 border-t space-y-4" style={{ borderColor: 'var(--border-color)' }}>
                                        {Object.entries(version.sections).map(([sectionName, items]) => (
                                            <div key={sectionName}>
                                                <h4 className="font-bold mb-2 flex items-center gap-2" style={{ color: getSectionColor(sectionName as any) }}>
                                                    <span>{getSectionIcon(sectionName as any)}</span>
                                                    {sectionName}
                                                </h4>
                                                <ul className="space-y-1 pl-6">
                                                    {items.map((item: string, idx: number) => (
                                                        <li key={idx} className="text-sm list-disc" style={{ color: 'var(--text-secondary)' }}>
                                                            {item}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
